// The reply flow's own calls and shapes (module 10 DESIGN B1554, B1557, B1561).
//
// They are here and not in `src/api.ts` because that file belongs to another track, and a reply
// flow that has to edit a shared client to add one field is how two sessions come to write one
// file. `ApiError` and `Unauthenticated` are the shared ones, so a caller still catches one kind
// of failure; only the request shapes are this track's.
import { viewFetch as fetch } from '../view-as.js';
import { ApiError, Unauthenticated, type Draft, type DraftRecipient, type DraftView, type Store, type VoiceNote } from '../api.js';

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const said = await res.json()
      .then((b: unknown) => (typeof b === 'object' && b !== null && typeof (b as { error?: unknown }).error === 'string' ? (b as { error: string }).error : null))
      .catch(() => null);
    throw new ApiError(res.status, said ?? `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

const post = <T>(path: string, body: unknown): Promise<T> =>
  fetch(path, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }).then((r) => json<T>(r));

/** One fact Cobrain put in front of the model, and the panel names under the draft (B1557). */
export interface DraftFact {
  id: string;
  kind: 'today' | 'own_meeting' | 'colleague_meeting';
  /** One line, as he reads it: "You have “Flight to Geneva” on 6 Oct at 18:05." */
  line: string;
}

/** What the draft leaned on, and what the writer asked for. Absent from a row written before migration 174. */
export interface DraftContext {
  /** The facts it LEANED ON, not the facts offered. Cobrain drops any id it did not send. */
  used: DraftFact[];
  /** How many were in front of it: the difference between "leaned on nothing" and "nothing to lean on". */
  offered: number;
  /** Where they came from, in his words: "From your calendar, the team's calendars and this thread." */
  source: string;
  /** Why the block is empty, when it is. Null where there is nothing to explain. */
  why: string | null;
  /** What he typed in the box before Cobrain wrote, kept so re-opening the panel still shows it. */
  instruction: string | null;
}

export interface ReplyDraftView {
  draft: Draft;
  voice: VoiceNote | null;
  state?: 'proposed' | 'edited' | 'sending' | 'sent' | 'discarded';
  sent?: { at: string; language: string } | null;
  channel?: string | null;
  recipients?: { to: DraftRecipient[]; cc: DraftRecipient[] } | null;
  reply?: { inThread: boolean; reason: string | null } | null;
  teams?: { kind: 'chat' | 'channel'; webUrl: string | null } | null;
  context?: DraftContext | null;
  book?: BookAsk | null;
  booking?: Booking;
}

export interface CreateDraft {
  all?: boolean;
  store: string;
  passItemId?: string;
  signalId?: string;
  purpose: Draft['purpose'];
  /** What he typed in the box, or what the option seeded and he left (B1553, B1554). Null when he pressed past it. */
  instruction?: string | null;
  /** The option's own language, carried at last: the API has declared it since it was written and never read it. */
  language?: string | null;
  /** The card option the draft is asked from (BUGS.md T26): the API reads who it goes to and the call it is about. */
  optionKey?: string | null;
}

export const replyDrafts = {
  /** POST /api/drafts. `instruction` is the sentence from the box; it is what D259 part 2 is for. */
  create: (body: CreateDraft) => post<{ store: Store } & ReplyDraftView>('/api/drafts', {
    ...body,
    ...(body.instruction ? { instruction: body.instruction } : {}),
    ...(body.language ? { language: body.language } : {}),
  }),
  edit: (id: string, store: string, bodyEn: string) => post<{ store: Store } & ReplyDraftView>(`/api/drafts/${encodeURIComponent(id)}`, { store, bodyEn }),
  recipients: (id: string, store: string, change: { removeTo?: string; addTo?: string; removeCc?: string; addCc?: string }) => post<{ store: Store } & ReplyDraftView>(`/api/drafts/${encodeURIComponent(id)}`, { store, ...change }),
  /**
   * POST /api/drafts/:id/send. `from`: the card option the draft answers; the API records the card's decision once the
   * draft has gone, and not before (actions contract row 1, REQ-2026-09-24-05).
   */
  send: (id: string, store: string, language: string, from: { passItemId: string; optionKey: string } | null = null, book?: BookAsk) =>
    post<{ store: Store; booking?: Booking } & DraftView>(`/api/drafts/${encodeURIComponent(id)}/send`, { store, language, ...(from ? { passItemId: from.passItemId, optionKey: from.optionKey } : {}), ...(book ? { book } : {}) }),
};

/**
 * What the panel says under the draft (B1557). One place, so the empty state cannot be drawn as a
 * quiet day on one screen and explained on another.
 *
 * The closing line counts the facts and says where they came from in HIS words, never the source
 * system's: "Four facts, from your calendar, the team's calendars and this thread. Nothing else was
 * read."
 */
export function leanedWords(c: DraftContext | null | undefined): { facts: DraftFact[]; line: string } {
  const used = c?.used ?? [];
  if (used.length > 0) {
    const n = used.length === 1 ? 'One fact' : `${used.length} facts`;
    // The API's sentence is "From your calendar and this thread." -- joined here as a clause, so
    // the count and the source are one sentence rather than two that read as a list of two things.
    const where = (c?.source ?? '').trim().replace(/\.$/, '').replace(/^From /, 'from ');
    return { facts: used, line: `${n}, ${where || 'from what Cobrain holds'}. Nothing else was read.` };
  }
  // Nothing leaned on. Why matters, and an empty block that says nothing reads as a quiet day.
  if (c?.why) return { facts: [], line: c.why };
  if ((c?.offered ?? 0) > 0) return { facts: [], line: 'Cobrain read your calendar and leaned on none of it for this reply.' };
  return { facts: [], line: 'Nothing beyond the message itself went into this.' };
}

/** What went, as POST /api/triage/items/:id/reply answers it (actions contract row 1; decide/typed-reply.ts). */
export interface TypedSent {
  channel: 'email' | 'teams';
  actionId: string;
  signalId: string;
  inThread: boolean;
  to: string[];
  cc: string[];
  webUrl: string | null;
  at: string;
}

/**
 * BUGS.md T69: the time the newest mail offers, as GET /api/drafts/offer/:signalId answers it. Every time is an instant;
 * the box writes them in `zone`, the person's own.
 */
export interface OfferView {
  signalId: string; startsAt: string; minutes: number; minutesFrom: 'thread' | 'default';
  zone: string; readZone: string; zoneWhy: string | null; said: string; from: string | null;
  /** Not answered the day it came: the press holds the time as tentative and invites nobody. */
  late: boolean; waitedDays: number;
  busy: Array<{ start: string; end: string }>;
}
export const offerRead = (store: string, signalId: string): Promise<{ store: Store; offer: OfferView | null }> =>
  fetch(`/api/drafts/offer/${encodeURIComponent(signalId)}?store=${encodeURIComponent(store)}`, { credentials: 'same-origin' }).then((r) => json(r));

/** What the same press booked: the event, or the words for why nothing was. */
export type Booking =
  | { status: 'booked' | 'held'; graphEventId: string; webLink: string | null; joinUrl: string | null; startsAt: string; endsAt: string; invited: string[]; words?: string }
  | { status: 'not_booked'; words: string };
/** The booking a Send carries: the time, its length, and whether it is only held (a late answer). */
export interface BookAsk { startsAt: string; minutes: number; hold: boolean; replaces?: true }

/** The same typed send used by the existing Reply, with the caller's complete box lists and choice. */
export const sendBoxReply = (body: { store: string; passItemId: string; signalId: string; all: boolean; text: string; to: DraftRecipient[]; cc: DraftRecipient[]; book?: BookAsk }) =>
  post<{ store: Store; sent: TypedSent; booking?: Booking }>('/api/drafts/reply', body);

/**
 * Actions contract row 15, "Correct it" (B1843): the deal's stage and value as he sets them, written as the person's (D40) through
 * the item page's own override route, which logs the change as his correction (D116: Cobrain learns from it).
 */
export const correctDeal = (store: string, passItemId: string, change: { stageCode?: string; value?: { amount: number; currency: string } }) =>
  post<{ store: Store; dealId: string; version: number }>(`/api/triage/items/${encodeURIComponent(passItemId)}/deal`, { store, ...change });

export const typedReply = {
  /** "Reply to <name>": the text as he typed it. The API sends it, then records the decision; the card then clears. */
  send: (passItemId: string, body: { store: string; optionKey: string; text: string }) =>
    post<{ store: Store; passItemId: string; sent: TypedSent; decision: { id: string; action: string } | null; item: null }>(`/api/triage/items/${encodeURIComponent(passItemId)}/reply`, body),
};

/**
 * Actions contract row 2 (Mike, 2026-09-24, answer 28, "Start with 'Cobrain,'"): "my own words go as typed, and a
 * message starting "Cobrain," is an instruction to draft". The instruction without its opening word ('' when nothing
 * follows it), or null when the text is his own reply. The API refuses to send an instruction as typed
 * (decide/typed-reply.ts cobrainInstruction), so the two agree on what counts.
 * BUGS.md T50 (Mike by pop-up, 6 Oct 2026, "Both Draft and Cobrain"): "Draft" counts as "Cobrain" does, either word
 * at the start, any case, followed by ; : , or a space; a word that only begins with either, or either alone, is his reply.
 */
export function instructionOf(text: string): string | null {
  const m = /^\s*(?:cobrain|draft)(?:\s*[;:,]|\s)\s*([\s\S]*)$/i.exec(text);
  return m ? m[1]!.trim() : null;
}

/** One message of a thread, as GET /api/drafts/thread/:signalId answers it (actions contract row 7). */
export interface ThreadMessageView {
  fromAddress?: string | null;
  attachments?: Array<{ id: string; name: string; text: string | null }>;
  id: string; kind: string; direction: string; occurredAt: string; subject: string | null; from: string | null; body: string;
  /** The message as a person reads it: quotes, signature and footer folded away (module 10 U414). */
  shown: string; folded: Array<{ kind: 'quoted' | 'signature' | 'footer'; text: string }>;
  /** Named recipients on the message's To and Cc lines. */
  to: Array<{ name: string; address: string | null }>; cc: Array<{ name: string; address: string | null }>;
}

/** BUGS.md T74: an attachment's name pressed opens the file itself, fetched from its mailbox on the press (module 10 U427). */
export const attachmentUrl = (store: string, attachmentId: string): string =>
  `/api/drafts/attachments/${encodeURIComponent(attachmentId)}?store=${encodeURIComponent(store)}`;

/** "Open the thread", "Open what you sent": the thread a message belongs to, as the person may read it. */
export interface ThreadView { subject: string | null; messages: ThreadMessageView[]; newestId?: string | null; earlier?: number; selfAddresses?: string[] }
type ThreadAnswer = { store: Store; thread: ThreadView };
type ThreadWaiter = { resolve: (answer: ThreadAnswer) => void; reject: (error: unknown) => void };
const pendingThreads = new Map<string, Map<string, ThreadWaiter[]>>();

/** Only coalesce this render's reads; no thread data survives into another render or store. */
async function readThreadBatch(store: string, entries: Array<[string, ThreadWaiter[]]>): Promise<void> {
  try {
    const ids = entries.map(([id]) => id).join(',');
    const answer = await fetch(`/api/drafts/threads?store=${encodeURIComponent(store)}&ids=${encodeURIComponent(ids)}`, { credentials: 'same-origin' })
      .then((r) => json<{ store: Store; threads: Record<string, ThreadView | null> }>(r));
    for (const [id, waiters] of entries) {
      const thread = answer.threads[id] ?? answer.threads[id.toLowerCase()];
      for (const waiter of waiters) {
        if (thread == null) waiter.reject(new ApiError(404, 'That message is not one you may read.'));
        else waiter.resolve({ store: answer.store, thread });
      }
    }
  } catch (error) {
    for (const [, waiters] of entries) for (const waiter of waiters) waiter.reject(error);
  }
}

export function threadRead(store: string, signalId: string, before?: string): Promise<ThreadAnswer> {
  if (before) return fetch(`/api/drafts/thread/${encodeURIComponent(signalId)}?store=${encodeURIComponent(store)}&before=${encodeURIComponent(before)}`, { credentials: 'same-origin' }).then((r) => json(r));
  let pending = pendingThreads.get(store);
  if (!pending) {
    pending = new Map();
    pendingThreads.set(store, pending);
    const batch = pending;
    queueMicrotask(() => {
      pendingThreads.delete(store);
      const entries = [...batch];
      for (let i = 0; i < entries.length; i += 60) void readThreadBatch(store, entries.slice(i, i + 60));
    });
  }
  return new Promise((resolve, reject) => {
    const waiters = pending.get(signalId) ?? [];
    waiters.push({ resolve, reject });
    pending.set(signalId, waiters);
  });
}

/** One free slot a block option offers (actions contract row 9). */
export interface FreeSlot { startsAt: string; endsAt: string }

/** Block time: his free slots of the block's length, read from his own calendar before anything is booked. */
export const blockSlots = (store: string, passItemId: string, optionKey: string): Promise<{ store: Store; slots: FreeSlot[] }> =>
  fetch(`/api/triage/items/${encodeURIComponent(passItemId)}/slots?store=${encodeURIComponent(store)}&optionKey=${encodeURIComponent(optionKey)}`, { credentials: 'same-origin' }).then((r) => json(r));
